import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type ConfirmOptions = {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" for permanent actions (red confirm), "default" otherwise. */
  tone?: "danger" | "default";
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

/**
 * Brand confirm dialog in place of `window.confirm`. Render `dialog` once in the component;
 * `await confirm({...})` resolves true on confirm, false on cancel / close.
 */
export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  dialog: React.ReactElement;
} {
  const [pending, setPending] = React.useState<Pending | null>(null);

  const confirm = React.useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...options, resolve })),
    []
  );

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const danger = (pending?.tone ?? "danger") === "danger";

  const dialog = (
    <Dialog open={pending != null} onOpenChange={(open) => (!open ? close(false) : undefined)}>
      <DialogContent className="gap-4 p-0 sm:max-w-md" data-confirm-dialog="">
        <DialogHeader className="gap-2 px-6 pt-5 pb-0 pr-12 text-left">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            {pending?.title}
          </DialogTitle>
          {pending?.description ? (
            <DialogDescription className="font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white/80">
              {pending.description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter
          showCloseButton={false}
          className="mx-0 mb-0 mt-0 w-full flex-row flex-wrap justify-end gap-2 rounded-b-[6px] border-t border-border bg-muted/50 px-6 py-4"
        >
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-4"
            onClick={() => close(false)}
          >
            <span className="normal-case tracking-normal">{pending?.cancelLabel ?? "Cancel"}</span>
          </Button>
          <Button
            type="button"
            variant={danger ? "outline-red" : "gradient"}
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-4"
            autoFocus
            onClick={() => close(true)}
            data-confirm-ok=""
          >
            <span className="normal-case tracking-normal">{pending?.confirmLabel ?? (danger ? "Delete" : "Confirm")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { confirm, dialog };
}
